import React, { useEffect, useState } from 'react';
import { useSupport } from '../../contexts/SupportContext';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { MessageCircle, Mail, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { brand } from '../../config/brand';
import { Link, useLocation } from 'react-router-dom';
import LandingNavbar from '../../components/LandingNavbar';
import { SupportForm } from '../../components/SupportModal';

export default function SupportPage() {
    const { openSupport } = useSupport();
    const { user } = useGlobalState();
    const location = useLocation();

    // Check if we should show the form directly or the landing view
    const showTicketForm = location.pathname === '/support/ticket';

    return (
        <div className="min-h-screen bg-background text-foreground">
            <LandingNavbar />

            <div className="flex flex-col items-center justify-center p-6 text-center">
                <div className="max-w-4xl w-full py-20">
                    {showTicketForm ? (
                        <div className="max-w-xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="flex items-center justify-between mb-8">
                                <Button variant="ghost" asChild className="text-muted-foreground hover:text-foreground">
                                    <Link to="/support">
                                        <ArrowLeft className="mr-2 h-4 w-4" />
                                        Back to Center
                                    </Link>
                                </Button>
                            </div>

                            <div className="bg-card p-8 sm:p-12 rounded-[3rem] border border-border shadow-2xl text-left relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-8 opacity-5">
                                    <MessageCircle size={160} />
                                </div>
                                <div className="relative z-10">
                                    <div className="mb-8">
                                        <h2 className="text-4xl font-black text-foreground italic uppercase tracking-tighter">Submit a Ticket</h2>
                                        <p className="text-muted-foreground font-medium">Complete the form below and we'll help you out.</p>
                                    </div>
                                    <SupportForm embedded={true} />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="max-w-2xl mx-auto space-y-12">
                            <div className="space-y-4">
                                <div className="bg-primary/10 w-24 h-24 rounded-[2rem] flex items-center justify-center mx-auto border border-primary/20 shadow-2xl shadow-primary/10 mb-8">
                                    <MessageCircle className="h-12 w-12 text-primary" />
                                </div>
                                <h1 className="text-6xl font-black text-foreground tracking-tighter italic uppercase">
                                    Support <span className="text-primary text-3xl align-top">Center</span>
                                </h1>
                                <p className="text-xl text-muted-foreground font-medium max-w-lg mx-auto leading-relaxed">
                                    We're here to help you dominate social media. Ask us anything, report a bug, or request a feature.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12 text-left">
                                <div className="bg-card p-8 rounded-[2rem] border border-border hover:border-primary/30 transition-all group overflow-hidden relative">
                                    <div className="absolute -right-4 -bottom-4 text-white/5 group-hover:text-white/10 transition-colors">
                                        <Mail size={120} />
                                    </div>
                                    <h3 className="text-xl font-black text-foreground mb-3 italic">Email Support</h3>
                                    <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                                        Prefer direct email? Reach out to our team anytime. We usually respond in under 24 hours.
                                    </p>
                                    <a href={`mailto:${brand.email}`} className="text-primary font-bold hover:underline">
                                        {brand.email}
                                    </a>
                                </div>

                                <div className="bg-card p-8 rounded-[2rem] border border-border hover:border-primary/30 transition-all group overflow-hidden relative">
                                    <div className="absolute -right-4 -bottom-4 text-white/5 group-hover:text-white/10 transition-colors">
                                        <ShieldCheck size={120} />
                                    </div>
                                    <h3 className="text-xl font-black text-foreground mb-3 italic">Submit a Ticket</h3>
                                    <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                                        Reach out to our team directly for technical assistance or to suggest new features.
                                    </p>
                                    <Button
                                        asChild
                                        className="bg-primary hover:bg-primary/90 text-white font-black h-12 rounded-xl px-6"
                                    >
                                        <Link to="/support/ticket">OPEN TICKET</Link>
                                    </Button>
                                </div>
                            </div>

                            <div className="pt-12 text-muted-foreground font-medium text-xs tracking-widest uppercase">
                                {brand.name} • {brand.tagline}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

